import CustomField, { EntityCustomField } from 'App/resources/Model/CustomField';
import { type FieldOverride } from 'Store/reducers/EditListenerReducer';
import { getCalculatingFieldTooltip } from 'App/helpers/formulaText';
import { Block, Flex, Label, Tooltip } from '@upsales/components';
import type { AllIWantData } from 'App/resources/AllIWant';
import BemClass from '@upsales/components/Utils/bemClass';
import Order from 'App/resources/Model/Order';
import { InputProps } from '../FormObserver';
import T from 'Components/Helpers/translate';
import React from 'react';
import getPlaceholder from 'Components/Helpers/getPlaceholder';
import LegacyCustomFieldInput from 'App/babel/components/LegacyReactTemplates/customFieldInput';
import customFieldDefaultOverride from 'App/helpers/customFieldDefaultOverride';

type Props = {
	field: CustomField & EntityCustomField;
	stageId?: number;
	inputProps?: { [key: string]: InputProps<any> };
	isDisabledField?: (fieldName: string) => boolean | undefined;
	disabled?: boolean;
	clientId?: number;
	type: keyof AllIWantData['customFields'];
	onChange: (fieldId: number, value: string) => void;
	className?: string;
	getCustomFieldOverrides?: (fieldId: number) => FieldOverride;
};

const CustomFieldsContentItem = ({
	field,
	stageId,
	inputProps,
	isDisabledField,
	disabled = false,
	clientId,
	type,
	onChange,
	className,
	getCustomFieldOverrides
}: Props) => {
	const classNames = new BemClass('CustomFields', className);
	const fieldWithStage = field as unknown as CustomField & {
		stages?: (Order['stage'] & { required: boolean })[];
	};

	const requiredInStage = !!(stageId && fieldWithStage.stages?.find(stage => stage.id === stageId && stage.required));
	const state = inputProps?.[`custom.Custom_${field.id}`]?.state;
	const maxLength =
		['Link', 'Email', 'String', 'Text', 'Currency'].indexOf(field.datatype) >= 0 ? field.maxLength : 0;
	const fieldDisabled = isDisabledField?.(`custom.${field.id}`) ?? (!field.editable || disabled);
	const fieldRequired = (!!field.obligatoryField || requiredInStage) && (field.value || !fieldDisabled);

	const customFieldOverride = getCustomFieldOverrides?.(field.id);
	const isDropdown = ['Select', 'MultiSelect'].indexOf(field.datatype) >= 0;
	const inputField = {
		...field,
		...(customFieldOverride?.default && {
			default: customFieldDefaultOverride(field.default, customFieldOverride.default)
		}),
		...(isDropdown &&
			customFieldOverride?.dropdownDefault && {
				dropdownDefault: customFieldDefaultOverride(field.dropdownDefault, customFieldOverride.dropdownDefault)
			})
	};

	return (
		<Block key={`cfw-${field.id}`} space="mbxl">
			<Flex justifyContent="space-between" alignItems="center">
				<Label
					maxLength={maxLength}
					value={typeof field.value === 'string' ? field.value : undefined}
					maxLengthReachedText={maxLength ? T('default.characterLimitReached') : undefined}
					required={!!fieldRequired}
					className={classNames.elem('label').mod({ 'has-progress': maxLength }).b()}
				>
					{customFieldOverride?.name ?? field.name}
				</Label>
			</Flex>
			<Tooltip
				title={
					field.datatype === 'Calculation'
						? getCalculatingFieldTooltip(field.formula, field.name)
						: field.tooltip || ''
				}
				disabled={!field.tooltip && (field.datatype !== 'Calculation' || !field.formulaVisible)}
			>
				<LegacyCustomFieldInput
					usenewdate
					useNewTime
					useNumberInput
					key={'cf-' + field.id}
					isNew
					field={inputField}
					state={state}
					entity={type}
					name={`custom.Custom_${field.id}`}
					placeholder={customFieldOverride?.placeholder ?? getPlaceholder(field.datatype) ?? field.name}
					disabled={fieldDisabled}
					useExternalDisabled
					useDebounce={false}
					valueChange={(value: string) => {
						if (Array.isArray(value) && value.length === 0 && field.datatype !== 'MultiSelect') {
							value = '';
						}
						onChange(field.id, value);
					}}
				/>
			</Tooltip>
		</Block>
	);
};

export default CustomFieldsContentItem;
